/**
 * Le AZIONI su un incontro Limulo: quando si possono fare, e come si capisce com'e' andata
 * quando il server rifiuta senza dire perche'.
 *
 * ⚠️ IL FATTO CHE DA' FORMA A TUTTO QUESTO FILE, misurato il 2026-09-06 leggendo
 * `qlash-limulo@origin/staging` (46404fc) e `qTournaments@origin/main` (7b773d4):
 *
 *   **Ogni rifiuto NOMINATO del provider arriva al client come `424
 *   E_QLASH_PROVIDER_UNAVAILABLE`, indistinguibile da «nessun provider».**
 *
 * `resolveRuntimeCapability` (`packages/backend/src/modules/app-runtime.ts:1653-1796`) avvolge
 * ogni candidato in `try/catch` e nel `catch` fa `errors.push(formatCapabilityResolverError(...))`:
 * appiattisce l'errore in una STRINGA e passa al candidato successivo. Esauriti i candidati
 * ritorna `{provider: null}`, e il BFF su `!result?.provider` lancia il 424
 * (`app-qlash-api/backend/router.js:222-231`) mettendo il motivo in `internalDetails`, che
 * `sendPublicError` **logga e non serializza mai**. Quindi «qualcuno ha gia' refertato un
 * punteggio diverso» (che il dominio dichiara `http: 409`, `core/risultati.ts:191-198`),
 * «punteggio oltre il tetto», «non hai una parte in questo torneo» e «questo torneo non
 * esiste» sono, per chi guarda lo schermo, **la stessa risposta**.
 *
 * Su una GET quel dubbio costa poco: si mostra il degrado onesto e si aspetta. Su una POST
 * costa tantissimo, perche' l'utente deve decidere **se ritentare** — e ritentare un rifiuto
 * definitivo, o non ritentare una corsa persa, sono due danni diversi e opposti.
 *
 * 🔑 LA MOSSA CHE CHIUDE IL BUCO SENZA TOCCARE IL BACKEND (or35, 06/09): **dopo un rifiuto
 * si RILEGGE.** La verita' e' leggibile anche quando il rifiuto non parla. Se dopo il rifiuto
 * il referto c'e' col NOSTRO punteggio, l'azione era passata (corsa, doppio invio, timeout
 * sulla risposta e non sulla scrittura); se c'e' con un punteggio DIVERSO, quello era il 409
 * travestito e lo si puo' finalmente nominare; se non c'e', allora e' il dubbio vero e la
 * copy lo ammette. E' la stessa mossa del refresh: non si indovina, si guarda.
 *
 * ⚠️ La riconciliazione **non e' una prova di successo**: dice cosa il server HA, non cosa la
 * nostra richiesta ha fatto. Su un referto il nostro punteggio identifica l'atto abbastanza
 * bene; su un check-in NO — v. `reconcileCheckin`, che per questo risponde meno.
 *
 * Nessun `import.meta.env`, nessun `@/env`, nessun `fetch` catturato: le chiamate arrivano
 * come PARAMETRI, come in `lib/limulo/auth.ts`, cosi' `tools/verify-limulo.ts` puo'
 * esercitare tutto sotto Node.
 */

import type {
  LimuloMatch,
  LimuloMatchCheckInOutcome,
  LimuloMatchResult,
  LimuloMatchResultPayload,
} from '../../types/limulo.ts';
import { limuloFailure, matchReasonOf, type LimuloFailure, type LimuloMatchReason } from './errors.ts';
import {
  LIMULO_RESULT_MODE_PROVIDER,
  matchStateOf,
  proposalRoleOf,
  type LimuloMatchState,
  type LimuloProposalRole,
} from './match.ts';
import { isTournamentTerminal } from './polling.ts';
import type { Presentazione } from './presentazione.ts';

// ─────────────────────────────────────────────────────────────────────────────
// 1. Quando un'azione si puo' fare — e, quando no, PERCHE'
// ─────────────────────────────────────────────────────────────────────────────

export type LimuloMatchAction = 'checkin' | 'result' | 'forfeit';

/**
 * Il motivo per cui un'azione non e' disponibile. Esiste perche' la decisione di prodotto
 * (Luca, 06/09) e' «bottoni PRESENTI con stato non disponibile onesto e motivo, mai sezione
 * nascosta»: un bottone che sparisce non dice niente, e chi lo cercava pensa di sbagliarsi.
 *
 * `server-says-no` e' il caso che merita il nome piu' brutto: lo stato dell'incontro
 * permetterebbe l'azione, ma la facciata non concede il permesso. Non sappiamo perche' — e
 * dire «non e' il momento» sarebbe inventare.
 */
export type LimuloActionBlock =
  | 'bye'
  | 'incomplete'
  | 'settled'
  | 'server-says-no'
  /**
   * 🔴 **Il torneo non e' in corso** (kb#303 §3, qTournaments 1.83.x): le scritture del giocatore su un
   * torneo non `in_progress` rispondono 409 `incontro.torneo-non-in-corso`, e i tre permessi si spengono.
   * ⚠️ E' SOLO un MOTIVO: vale quando il server ha GIA' detto no (il permesso e' `false`), e non accende
   * ne' spegne niente. Senza, lo stesso `false` si leggeva «il server non dice perche'» (`server-says-no`)
   * mentre il perche' lo sappiamo dallo stato del torneo.
   */
  | 'torneo-non-in-corso'
  /** Lo stesso, su un torneo TERMINATO: la frase non dice «in questo momento» (revisione 2 di #474). */
  | 'torneo-terminato'
  /**
   * 🔴 **Il check-in lo abbiamo GIA' mandato noi, e il server l'ha accettato.**
   *
   * Misurato il 2026-09-06 (or45, `stg-tma`, Ciao Cup): dopo un check-in andato a buon fine
   * — `200 ok:true`, `checkins[0].checkedIn === true` sul server — la facciata continua a
   * rispondere **`canCheckIn: true`**. Il bottone resta acceso e invita a un secondo tap che
   * il server accoglie come «gia'-registrato» (`catalogo.js:193`), cioe' un giro di rete che
   * non cambia niente e che l'utente legge come «non ha funzionato».
   *
   * ⚠️ **E questa NON e' una deduzione d'identita'.** Non stiamo confrontando id di parte con
   * `me.id` — quella e' proprio la cosa che non si puo' fare (ADR-0017, e i partecipanti non
   * portano `userId`). L'informazione e' l'**esito della nostra POST in questa sessione**:
   * l'unico fatto su di noi che abbiamo il diritto di conoscere, perche' l'abbiamo prodotto.
   * Il server resta l'autorita' su tutto il resto — infatti `settled` batte questo.
   */
  | 'checkin-done'
  /**
   * 🔴 **Il check-in risulta gia' fatto, e lo dice il SERVER** (`myCheckin === true`,
   * `qlash-limulo#134`). Stessa cosa di `checkin-done` vista dall'altra parte: quello e' l'esito
   * della nostra POST in QUESTA sessione, questo e' il fatto dichiarato dal server, e vale anche
   * dopo una ricarica. Il contratto lo dice cosi': il check-in si mostra solo con `canCheckIn &&
   * myCheckin === false`. ⚠️ Scatta SOLO sul `true` esplicito: `null`/assente e' «non lo sappiamo».
   */
  | 'already-checked-in'
  /** Referto proposto da me: aspetto la conferma dell'avversario (kb#146). */
  | 'awaiting-confirmation'
  /**
   * 🔴 **I due referti non coincidono: l'incontro e' fermo e decide lo staff** (kb#146 §5).
   *
   * ⚠️ **La disputa NON scade** — a differenza della proposta, che dopo 15 minuti si applica
   * da sola. Non c'e' niente che il giocatore possa fare da qui per sbloccarla, e questo e'
   * il motivo per cui il blocco porta un nome suo invece di riusare `settled`: «c'e' gia' un
   * referto» inviterebbe a rileggere, mentre il fatto e' che *l'attesa ha un proprietario, e
   * non e' chi guarda la schermata*.
   */
  | 'disputed'
  /**
   * 🔴 **Il risultato di questo incontro lo manda il GIOCO, non il giocatore**
   * (`resultMode: 'provider'`). Il motore rifiuta la registrazione di un giocatore (C25) e gli
   * lascia solo il forfeit (C27) `[T @gionko, kb#146, 2026-09-10T00:15:25Z]`.
   *
   * ⚠️ **E il permesso del server oggi NON lo sa**: `canSubmitResult` non guarda `resultMode`
   * (C26 del registro, difetto dichiarato da chi possiede il motore e in chiusura), quindi in
   * `provider` puo' uscire **`true`** e il referto prende un rifiuto. E' la forma esatta del
   * caso che questo file esiste per non produrre: *un bottone che porta a un rifiuto garantito
   * costa un tap, non spiega niente e insegna a non fidarsi.* ⇒ qui il modo batte il permesso,
   * e resta a batterlo anche il giorno in cui il motore correggera' C26.
   */
  | 'provider-result'
  /**
   * 🔴 **Il referto aspetta i due check-in** (qTournaments 1.69.0, ADR-0158): «the player's result
   * is refused until both sides have checked in» (`QLASH-TOURNAMENT-PUBLIC-DATA.md` @
   * `qlash-limulo#134`).
   *
   * ⚠️ **Non e' una deduzione, e le condizioni sono tre, tutte dichiarate dal server**: il permesso
   * dice no (`canSubmitResult: false`), la finestra del check-in e' richiesta e APERTA, e `readyAt`
   * e' ancora `null` (i due lati non risultano presenti). Si legge `matchCheckIn`, non `checkins[]`
   * ne' l'orologio. Senza `matchCheckIn` (il servito di oggi) il ramo non scatta e resta la frase
   * neutra di sempre, `server-says-no`: «non dice perche'» e' vero finche' non lo dice.
   * ⛔ E con `canSubmitResult: true` non scatta MAI: il permesso del server batte la nostra lettura,
   * e se il motore rifiutasse lo stesso il motivo arriva nominato (`match.awaiting-check-in`).
   */
  | 'awaiting-check-in'
  /**
   * 🔴 **Il tempo del check-in e' scaduto, e il torneo lo sta chiudendo** (@gionko, kb#268
   * 2026-09-25T08:41:40Z): `matchCheckIn` richiesto, `open: false`, `outcome: null`, `closedAt:
   * null`. Dura fino al passaggio del battito (~60 s), e in quella finestra i due permessi sono
   * spenti. ⇒ «il server non dice perche'» sarebbe falso: lo dice, e dice «sta chiudendo».
   * ⚠️ Con `closedAt` valorizzato e `outcome` ancora `null` NON scatta: li' la chiusura c'e' stata,
   * e resta la frase neutra (la scheda dice «l'esito non e' ancora arrivato»).
   * ⛔ Col permesso `true` non scatta MAI, come `awaiting-check-in`: il permesso batte la lettura.
   */
  | 'checkin-closing'
  /**
   * 🔴 **Il torneo e' in pausa, e il check-in si decide quando riprende** (qTournaments 1.84.0,
   * `qlash-limulo#164`): la stessa forma di `checkin-closing`, ma col torneo che lo STATO dice in pausa
   * (`torneo.inPausa`). In pausa il motore non chiude niente, e alla ripresa sposta la scadenza della
   * durata del fermo SE la finestra era ancora aperta quando si e' fermato: allora si riapre. Se era
   * gia' scaduta prima del fermo, alla ripresa la chiude il battito ⇒ la frase non promette la
   * riapertura: dice solo che si decide alla ripresa, vero in entrambi i casi.
   */
  | 'checkin-in-pausa'
  /**
   * 🔴 **Nessuno dei due lati ha fatto il check-in: decide lo staff** (`matchCheckIn.outcome ===
   * 'double-no-show'`, incontro ancora pendente). Dalla qTournaments 1.70.2 `canSubmitResult` e'
   * `false` (fino alla 1.70.1 tornava `true`: difetto del motore contro le regole 5-7 di ADR-0158,
   * kb#268 08:41:40Z). ⛔ Mai «aspetta l'avversario»: non c'e' nessuno da aspettare, l'attesa ha
   * un proprietario e non e' chi guarda la schermata — la stessa ragione di `disputed`.
   * ⛔ Col permesso `true` non scatta MAI.
   */
  | 'staff-decides';

/**
 * Cio' che sappiamo NOI e il server non ci ridice. Non e' cache dello stato remoto: e' il
 * registro di cosa abbiamo fatto in questa sessione, e vale solo finche' la schermata vive.
 */
export interface LimuloLocalActionState {
  /** La nostra POST di check-in ha risposto «registrato» (`submitted`). */
  checkinDone?: boolean;
}

export interface LimuloActionAvailability {
  available: boolean;
  block: LimuloActionBlock | null;
}

const FLAG_OF: Record<LimuloMatchAction, (m: LimuloMatch) => boolean> = {
  checkin: (m) => m.canCheckIn,
  result: (m) => m.canSubmitResult,
  forfeit: (m) => m.canForfeit,
};

/**
 * La disponibilita' di UNA azione. **L'ordine dei rami E' la logica** e non si riordina.
 *
 * Lo STATO viene prima del permesso, e non e' pignoleria: quando il permesso e' negato, quasi
 * sempre e' lo stato a spiegarlo, e «il server non concede» detto al posto di «l'incontro e'
 * gia' concluso» e' una risposta peggiore ricavata dallo stesso dato.
 *
 * 🔴 E il ramo `settled` batte il permesso anche quando il permesso dice `true`. Non e'
 * sfiducia nella facciata: un secondo referto diverso e' un rifiuto **garantito** (il primo
 * vince — `core/risultati.ts:15`), ed e' esattamente il rifiuto che arriva MUTO (v. il
 * cappello). Un bottone che porta a un rifiuto muto certo e' la cosa peggiore che possiamo
 * mettere su questa schermata: costa un tap, non spiega niente, e insegna a non fidarsi.
 *
 * ⚠️ E l'ultimo ramo NON e' una formalita'. I tre flag passano da `Boolean(...)` nel
 * sanitizer (`policy.js:501-503`): un provider che non li emettesse li renderebbe `false`,
 * e la sezione diventerebbe di sola lettura **con 200 e nessun segnale**. Oggi
 * `app-qtournaments` li emette [M 2026-09-06, `superficie/capability/incontri.ts:230-232`];
 * il gate tiene un controllo positivo su quel fixture, cosi' il giorno che cadono lo dice.
 */
export function matchActionAvailability(
  match: LimuloMatch,
  action: LimuloMatchAction,
  locale: LimuloLocalActionState = {},
  torneo: LimuloTorneoDellIncontro = TORNEO_SCONOSCIUTO,
): LimuloActionAvailability {
  const state = matchStateOf(match);
  if (state === 'bye') return { available: false, block: 'bye' };
  if (state === 'incomplete') return { available: false, block: 'incomplete' };
  if (state === 'reported' || state === 'decided') return { available: false, block: 'settled' };
  // 🔴 LA DISPUTA SPEGNE IL REFERTO — **e solo quello**. Lato server `canSubmitResult` e'
  // `false` per entrambe le parti finche' la disputa e' aperta [M 2026-09-15 22:01:39Z, su una
  // disputa VERA], quindi senza questo ramo l'esito a schermo sarebbe comunque «non
  // disponibile» — ma col motivo sbagliato (`server-says-no`, cioe' «non sappiamo perche'»),
  // su un incontro in cui il perche' lo sappiamo benissimo. ⚠️ E non si delega la frase al
  // permesso: il giorno che la facciata smettesse di emettere quel `false`, un bottone
  // comparirebbe su un incontro che nessuno dei due puo' piu' muovere.
  //
  // 🔴 MA **CHECK-IN E FORFEIT RESTANO**, e questa riga e' nata da una misura che ha corretto
  // la stesura precedente: sulla disputa vera il server concede ancora **`canForfeit: true`**
  // e **`canCheckIn: true`** (`canSubmitResult` era l'unico `false`). La prima stesura
  // spegneva tutte e tre le azioni ⇒ ***toglieva al giocatore due porte che il server gli
  // lasciava aperte*** — lo specchio esatto del difetto che questo file esiste per togliere:
  // li' una schermata taceva, qui avrebbe tolto un'uscita. ⚠️ E il principio giusto l'avevo
  // gia' scritto dieci righe piu' sotto per `provider` (dove il forfeit e' l'unica porta che
  // resta, C27) e non l'avevo applicato qui: *la regola stretta sul caso accanto, quella larga
  // sul proprio.*
  if (state === 'disputed' && action === 'result') {
    return { available: false, block: 'disputed' };
  }
  // 🔴 REFERTO PROPOSTO: chi l'ha proposto aspetta, e le tre azioni ordinarie si spengono col
  // motivo giusto. Il ramo sta QUI, dopo `settled` e prima del permesso, per la stessa ragione
  // degli altri: quando il server nega, e' lo stato a spiegarlo, e «in attesa della conferma
  // dell'avversario» dice quello che `server-says-no` non direbbe.
  //
  // ⚠️ Chi deve CONFERMARE non passa di qui e non e' una svista: per lui `canSubmitResult` e'
  // `true` [T @gionko, kb#146 §1(i)] e l'azione `result` resta disponibile — il pannello che
  // gli si apre e' quello della conferma, e lo decide la schermata leggendo `proposalRoleOf`.
  // Se il ruolo non e' derivabile (`unknown`: `isMe` non arrivato) il ramo NON scatta e resta
  // il permesso del server: mai spegnere un'azione sulla base di un lato che non conosciamo.
  // ⚠️ `ruoloPropostaOf`, non `proposalRoleOf`: il ruolo e' dedotto da `canSubmitResult`, che la 1.83 spegne
  // per ENTRAMBI i lati a torneo non in corso — tutti e due sembrerebbero «chi ha proposto» (revisione 1 di #474).
  if (state === 'proposed' && ruoloPropostaOf(match, torneo) === 'proponent') {
    return { available: false, block: 'awaiting-confirmation' };
  }
  // ⚠️ PRIMA del permesso del server, e non dopo. Non e' sfiducia: e' che qui sappiamo il
  // motivo ESATTO («l'ho gia' fatto io, e ha funzionato»), mentre `server-says-no` e' il nome
  // che diamo a un rifiuto senza spiegazione. Fra due risposte vere si sceglie quella che
  // dice di piu'.
  // ⛔ E resta DOPO `settled`: se l'incontro e' concluso, quello e' il fatto che conta, e un
  // «check-in gia' registrato» su un incontro finito sarebbe una nota di colore.
  // 🔴 IL SERVER DICE CHE IL MIO CHECK-IN C'E' GIA' (`myCheckin === true`). Stessa posizione e
  // stessa ragione di `checkin-done` qui sotto — `canCheckIn` resta `true` anche dopo — ma vale
  // oltre la sessione. Sta PRIMA del flag locale: fra i due fatti veri, quello del server e' il piu'
  // forte, e la sua frase non dice «in questa sessione» su un check-in fatto ieri da un altro
  // telefono. ⛔ Solo sul `true`: con `null` (assente) si resta al permesso, come prima.
  if (action === 'checkin' && match.myCheckin === true) {
    return { available: false, block: 'already-checked-in' };
  }
  if (action === 'checkin' && locale.checkinDone === true) {
    return { available: false, block: 'checkin-done' };
  }
  // 🔴 IL MODO BATTE IL PERMESSO, e SOLO sul referto. In `provider` il forfeit resta l'unica
  // porta del giocatore (C27) e il check-in non c'entra: spegnerli tutti e tre qui sarebbe
  // togliere un'azione che il server concede davvero.
  // ⚠️ Si confronta col valore ESATTO. Un `!== 'manual'` spegnerebbe il referto anche quando il
  // campo e' vuoto — cioe' su ogni mirror piu' vecchio di 1.24.0, dove `resultMode` non usciva:
  // la sezione diventerebbe di sola lettura di colpo, con 200 e nessun segnale.
  if (action === 'result' && match.resultMode === LIMULO_RESULT_MODE_PROVIDER) {
    return { available: false, block: 'provider-result' };
  }
  // 🔴 IL PERMESSO NEGATO, SPIEGATO DALLA FINESTRA DEL CHECK-IN — solo sul referto, solo quando il
  // permesso e' davvero `false`, e solo coi tre fatti dichiarati (v. `awaiting-check-in`). Sta
  // DOPO `provider-result`: in quel modo il referto non esiste, e l'attesa non ha niente da aprire.
  if (action === 'result' && !match.canSubmitResult && awaitingCheckInOf(match)) {
    return { available: false, block: 'awaiting-check-in' };
  }
  // 🔴 IL PERMESSO NEGATO, SPIEGATO DALLA FINESTRA CHIUSA MA NON ANCORA DECISA (kb#268 08:41:40Z) —
  // su check-in e referto, e SOLO col permesso davvero `false`: il motore spegne quei due, non il
  // ritiro. Stanno DOPO `provider-result` (in provider il referto non esiste) e dopo
  // `already-checked-in`/`checkin-done`: quelli dicono del MIO check-in, che resta vero anche qui.
  // ⚠️ Le due forme sono disgiunte (una ha l'esito `double-no-show`, l'altra nessun esito): l'ordine
  // fra le due righe qui sotto non decide niente, e non va letto come una precedenza.
  if ((action === 'checkin' || action === 'result') && !FLAG_OF[action](match)) {
    if (checkInStaffDecidesOf(match)) return { available: false, block: 'staff-decides' };
    if (checkInClosingOf(match)) {
      return { available: false, block: torneo.inPausa ? 'checkin-in-pausa' : 'checkin-closing' };
    }
  }
  // kb#303 §3: SOLO a permesso gia' negato, e SOLO subito prima del motivo che non sa niente — i motivi
  // dell'incontro qui sopra dicono di piu' e restano loro. `inCorso: null` (torneo non arrivato) non e' «no».
  if (!FLAG_OF[action](match) && torneo.inCorso === false) {
    return { available: false, block: torneo.terminato ? 'torneo-terminato' : 'torneo-non-in-corso' };
  }
  if (!FLAG_OF[action](match)) return { available: false, block: 'server-says-no' };
  return { available: true, block: null };
}

/**
 * Il torneo di un incontro, ridotto alle due cose che servono alla scheda (kb#303 §3).
 *
 * - `inCorso`: `status === 'in_progress'`, cioe' lo STESSO predicato con cui il motore risponde 409
 *   `incontro.torneo-non-in-corso` (una lista degli stati «prima dell'avvio» qui non serve: qualunque
 *   stato diverso e' «non in corso» anche per il motore). `null` = il torneo non e' arrivato, o lo
 *   stato e' vuoto: «non lo so» non e' «non in corso».
 * - `terminato`: `isTournamentTerminal`, l'UNICA enumerazione dei terminali (quella dello storico).
 * - `inPausa`: `status === 'paused'`, e SOLO quello (vincolo di tma-orcr, 28/09): dalla qTournaments
 *   1.84.0 (`qlash-limulo#164`) la pausa ferma gli orologi degli incontri, e le frasi sul tempo lo
 *   dicono leggendo lo STATO, mai l'orologio. ⚠️ Il motore li ferma lasciando `in_progress` verso
 *   QUALUNQUE stato (`memoriaDelFermo`), ma `blocked` esce come `created` (`publicStateOf`): li' le
 *   frasi restano quelle di prima. E' un buco dichiarato, non una svista.
 *
 * ⚠️ Il BFF passa lo stato com'e' (`sanitizePublicTournament`: `tournament.status || tournamentStatus`).
 */
export interface LimuloTorneoDellIncontro {
  inCorso: boolean | null;
  terminato: boolean;
  inPausa: boolean;
}

export const TORNEO_SCONOSCIUTO: LimuloTorneoDellIncontro = { inCorso: null, terminato: false, inPausa: false };

export function torneoDellIncontroOf(torneo: unknown): LimuloTorneoDellIncontro {
  const status =
    torneo !== null && typeof torneo === 'object' ? (torneo as { status?: unknown }).status : undefined;
  if (typeof status !== 'string' || status === '') return TORNEO_SCONOSCIUTO;
  return { inCorso: status === 'in_progress', terminato: isTournamentTerminal(torneo), inPausa: status === 'paused' };
}

/**
 * Il tempo del check-in e' FERMO: il torneo e' in pausa (qTournaments 1.84.0, `qlash-limulo#164`) E il
 * server non concede il check-in.
 * 🔴 **La seconda meta' e' la cintura contro due letture sfasate** (revisioni 1 e 2 di #477): torneo e
 * incontro si leggono con due GET diverse, e fra le due c'e' fino a un giro di polling. In una lettura
 * coerente il motore non da' MAI `canCheckIn` a torneo in pausa (`siGiocaNelTorneo`, anche a chi e' gia'
 * pronto), quindi un permesso acceso accanto a «pausa» vuol dire che una delle due e' vecchia. Le
 * spiegazioni sono DUE, non una:
 *  · la pausa e' finita e il TORNEO non e' ancora stato riletto ⇒ il conto vero e' giusto;
 *  · la pausa e' appena cominciata e l'INCONTRO non e' ancora stato riletto ⇒ si vede il conto sulla
 *    scadenza vecchia con «Sono pronto» acceso, e il tocco prende 409 (`incontro.torneo-non-in-corso`).
 * Nessuna delle due dura piu' di un giro. Si segue il permesso perche' fra i due errori e' quello che non
 * costa l'incontro: coprire un gesto concesso con una nostra lettura e' il verso che perde a tavolino.
 */
export function tempoCheckInFermoOf(match: LimuloMatch, torneo: LimuloTorneoDellIncontro): boolean {
  return torneo.inPausa && !match.canCheckIn;
}

/**
 * Torneo terminato e nessuna delle tre azioni concessa: al posto del pannello, una riga («il torneo e'
 * terminato»), non un check-in spento (vincolo 3 di tma-orcr su kb#303 §3).
 * ⚠️ I tre permessi si guardano lo stesso: se il server ne concede uno, il pannello resta — la riga non
 * spegne niente che il server permette.
 */
export function torneoTerminatoSenzaAzioni(match: LimuloMatch, torneo: LimuloTorneoDellIncontro): boolean {
  // SOLO un incontro senza risultato: un referto proposto o contestato ha nel pannello i due referti e chi li ha
  // scritti, e la riga li nasconderebbe (revisione 1 di #474, punto 2).
  const state = matchStateOf(match);
  if (state !== 'pending' && state !== 'incomplete') return false;
  return torneo.terminato && !match.canCheckIn && !match.canForfeit && !match.canSubmitResult;
}

/**
 * Il ruolo sulla proposta di referto, per le decisioni della scheda e della barra dell'app.
 * 🔴 `proposalRoleOf` lo DEDUCE da `canSubmitResult` (`match.ts`); a torneo non in corso la 1.83 spegne quel
 * permesso per entrambi i lati, e la deduzione direbbe «hai proposto tu» anche a chi deve confermare. Li'
 * il ruolo non si deduce: `unknown`, cioe' il «non lo so» che la scheda gia' sa dire.
 */
export function ruoloPropostaOf(match: LimuloMatch, torneo: LimuloTorneoDellIncontro): LimuloProposalRole {
  return torneo.inCorso === false ? 'unknown' : proposalRoleOf(match);
}

/**
 * La chiave dell'etichetta di stato di un incontro (intestazione della scheda e riga dell'elenco): UNA
 * funzione per le due, o divergono.
 * 🔴 A torneo TERMINATO un incontro senza risultato non e' «da giocare» ne' «da comporre»: non lo sara' piu'
 * (reperto su stg 28/09, trasportato da tma-orcr: «ROUND 1 · TAVOLO 1 · DA GIOCARE» su un torneo
 * abbandonato). Gli stati che dicono qualcosa sul referto (`proposed`, `disputed`, …) restano loro.
 */
export function chiaveStatoIncontro(
  state: LimuloMatchState,
  torneo: LimuloTorneoDellIncontro,
  presentazione: Presentazione = 'torneo',
): string {
  if (torneo.terminato && (state === 'pending' || state === 'incomplete')) {
    // r15: la partita di una coda non e' un torneo che finisce, e' una partita che finisce.
    return presentazione === 'coda' ? 'matches.state.noResultMatchOver' : 'matches.state.noResultTournamentOver';
  }
  return `matches.state.${state}`;
}

/**
 * La finestra del check-in e' richiesta, aperta, e i due lati non risultano ancora presenti.
 * Legge SOLO `matchCheckIn` (gia' passato da `matchCheckInOf`): assente o `null` ⇒ `false`.
 */
export function awaitingCheckInOf(match: LimuloMatch): boolean {
  const ck = match.matchCheckIn ?? null;
  return ck !== null && ck.required && ck.open && ck.readyAt === null;
}

/**
 * Il tempo e' scaduto e il battito non e' ancora passato: richiesta, chiusa, senza esito e senza
 * `closedAt` (@gionko, kb#268 08:41:40Z). ⛔ Nessun orologio: e' il server a dire `open: false`.
 */
export function checkInClosingOf(match: LimuloMatch): boolean {
  const ck = match.matchCheckIn ?? null;
  return ck !== null && ck.required && !ck.open && ck.outcome === null && ck.closedAt === null;
}

/**
 * Doppia assenza lasciata allo staff: `outcome === 'double-no-show'` su una finestra richiesta.
 * ⚠️ `required` si guarda anche qui: con `required: false` la schermata e' quella di prima.
 */
export function checkInStaffDecidesOf(match: LimuloMatch): boolean {
  const ck = match.matchCheckIn ?? null;
  return ck !== null && ck.required && ck.outcome === 'double-no-show';
}

export function matchActionsOf(
  match: LimuloMatch,
  locale: LimuloLocalActionState = {},
  torneo: LimuloTorneoDellIncontro = TORNEO_SCONOSCIUTO,
): Record<LimuloMatchAction, LimuloActionAvailability> {
  return {
    checkin: matchActionAvailability(match, 'checkin', locale, torneo),
    result: matchActionAvailability(match, 'result', locale, torneo),
    forfeit: matchActionAvailability(match, 'forfeit', locale, torneo),
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// 2. Gli esiti delle chiamate, nella forma NATIVA di RTK Query
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Quello che si ottiene da `await trigger(args)`: `data` valorizzato in caso di successo,
 * `error` valorizzato in caso di rifiuto.
 *
 * Si prende la forma nativa invece di un `Result` nostro perche' ogni conversione al bordo
 * e' un posto in cui il ramo d'errore si perde per distrazione — e qui il ramo d'errore
 * **e' il contenuto** di tutto il file.
 *
 * 🔴 **I DUE CAMPI SONO OPZIONALI, E NON E' PIGRIZIA DI TIPI.** Una mutation risolve con
 * `{data}` OPPURE `{error}`, ma un **lazy query trigger** risolve con l'intero stato della
 * query — un oggetto che porta SEMPRE entrambe le chiavi, con `undefined` in quella che non
 * si applica. La prima stesura discriminava con `'error' in o`: su un lazy trigger quella
 * condizione e' vera **sempre**, anche in caso di successo, quindi ogni rilettura sarebbe
 * risultata fallita e ogni riconciliazione avrebbe risposto «nessun referto» — cioe' il
 * dubbio, sistematicamente, proprio nel caso in cui la verita' era li' da leggere. Il
 * compilatore l'ha fermata; il gate la tiene ferma (§ riletture, fixture in forma di lazy).
 * ⇒ **si discrimina sul VALORE (`!== undefined`), mai sulla presenza della chiave.**
 */
export type LimuloCallOutcome<T> = { data?: T; error?: unknown };

/**
 * ⚠️ **Esportati** (non piu' privati) perche' `lib/limulo/registration.ts` li RIUSA. Non e'
 * comodita': queste due righe portano la lezione qui sopra — si discrimina sul VALORE, e
 * `data` valorizzato non significa successo — e riscriverle nell'altro file significherebbe
 * rifare da capo lo stesso errore su una superficie che il compilatore non copre. Un
 * doppione di una difesa e' una difesa dimezzata.
 */
export const outcomeError = <T>(o: LimuloCallOutcome<T>): unknown | undefined =>
  o !== null && typeof o === 'object' && o.error !== undefined ? o.error : undefined;

/**
 * ⚠️ **`data` VALORIZZATO NON SIGNIFICA «e' andata bene».** Un lazy trigger di RTK Query
 * risolve con l'intero stato della query, e su un refetch FALLITO quello stato conserva
 * l'ULTIMO `data` buono accanto al nuovo `error`. La cache qui e' popolata per costruzione:
 * `LimuloMatchDetail` monta `useGetLimuloMatchQuery` con gli stessi argomenti del trigger.
 * ⇒ senza questa guardia una rilettura fallita verrebbe letta come riuscita, e la
 * riconciliazione riferirebbe uno stato VECCHIO con la faccia di «al momento risulta» —
 * cioe' il difetto che questo file esiste per non commettere, alla fine del percorso che
 * esiste per evitarlo.
 */
export const outcomeData = <T>(o: LimuloCallOutcome<T>): T | undefined =>
  o !== null && typeof o === 'object' && outcomeError(o) === undefined ? o.data : undefined;

/**
 * Un rifiuto che ha senso ritentare **da soli**, senza che l'utente cambi niente.
 *
 * ⛔ `domain-or-missing` NON e' ritentabile, e questa e' la riga che conta: e' il 424 che
 * inghiotte il motivo, e sotto ci sta anche «qualcuno ha gia' refertato». Ritentarlo
 * automaticamente significa ripetere all'infinito un rifiuto definitivo — e su un referto,
 * ripeterlo mentre l'utente crede che stia funzionando.
 *
 * ⛔ `payload-too-large` / `unsupported-media` non cambiano mai esito: e' il file a essere
 * sbagliato, e l'unico che puo' fare qualcosa e' chi lo ha scelto.
 *
 * ⛔ E NEMMENO `internal-or-switch-off`, che qui era di troppo. Il nome dice il dubbio:
 * `errors.ts` lo descrive come «guasto vero **oppure** interruttore d'organizzazione spento»,
 * e un interruttore e' configurazione — non cambia esito da solo, mai. Ritentarlo violava la
 * regola scritta due righe sopra usando come argomento la meta' piu' comoda di un verdetto
 * che ammette due letture.
 */
const RITENTABILI = new Set(['network', 'server', 'identity-provider-down']);

export function isRetryableFailure(failure: LimuloFailure): boolean {
  return RITENTABILI.has(failure.verdict);
}

// ─────────────────────────────────────────────────────────────────────────────
// 3. Referto + prova in UN tap — l'adattatore
// ─────────────────────────────────────────────────────────────────────────────

/** Com'e' andato il PRIMO passo (il punteggio), dopo la riconciliazione. */
export type LimuloResultOutcome =
  /** La POST e' andata a buon fine. */
  | 'submitted'
  /** La POST ha rifiutato, ma rileggendo il referto c'e' ed e' IL NOSTRO punteggio. */
  | 'already-ours'
  /** La POST ha rifiutato e il referto c'e' con un punteggio DIVERSO: era il 409 travestito. */
  | 'already-other'
  /** La POST ha rifiutato e rileggendo non c'e' nessun referto: il dubbio vero. */
  | 'refused';

/** Com'e' andato il SECONDO passo (la prova). `skipped` = non c'era un file da allegare. */
export type LimuloEvidenceOutcome = 'attached' | 'failed' | 'skipped';

export interface LimuloSubmitReport {
  result: LimuloResultOutcome;
  evidence: LimuloEvidenceOutcome;
  /** Il rifiuto del passo 1, se c'e' stato — anche quando la rilettura lo ha poi smentito. */
  resultFailure: LimuloFailure | null;
  /**
   * Il motivo NOMINATO del rifiuto del passo 1 (`details.matchReason`, `qlash-limulo#134`), o
   * `null`. Oggi e' sempre `null`: il bordo servito non lo porta.
   */
  resultMatchReason: LimuloMatchReason | null;
  /** Il rifiuto del passo 2, se c'e' stato. */
  evidenceFailure: LimuloFailure | null;
  /** Il punteggio che il server HA, quando la rilettura e' riuscita a leggerlo. */
  serverScore: { home: number; away: number } | null;
  /**
   * Il `resultType` del referto che il server HA dopo l'invio (`proposed`, `disputed`, …), o
   * `null` se la risposta non lo porta. 🔑 E' l'unico modo di sapere COSA e' diventato il mio
   * invio (ADR-0138): una proposta, una conferma o una DISPUTA — la scrittura della disputa
   * risponde con la busta riletta, `resultType: 'disputed'` (`bustaDiScrittura`, qTournaments).
   * Dedurlo da cio' che lo schermo sapeva prima dell'invio sbaglia quando l'altro lato ha appena
   * proposto (il polling e' a 12 s) o quando lo staff ha ritrattato la proposta.
   */
  serverResultType: string | null;
  /**
   * Quante volte il passo 2 e' stato RITENTATO (0 = riuscito al primo colpo, o mai tentato).
   * ⚠️ Non confonderlo col `evidenceMaxRetries` dell'input: quello e' il TETTO, questo e' il
   * conteggio. Si chiamavano uguale, e due nomi identici con significati opposti a due schermi
   * di distanza sono un errore che si scrive da solo.
   */
  evidenceRetries: number;
}

export interface LimuloScore {
  home: number;
  away: number;
}

/**
 * `scoreA`/`scoreB` del referto contro il punteggio che abbiamo mandato.
 *
 * ⚠️ `scoreA`/`scoreB` sono `number | null`: un `null` NON combacia con nessun numero, e
 * trattarlo come `0` farebbe dire «e' il tuo» a un referto che il punteggio non ce l'ha.
 * L'accoppiamento e' `home → scoreA`, `away → scoreB` (`incontri.ts:refertoAnnidatoDi`,
 * `scoreA: numero(doc,'scoreHome')`).
 */
export function sameScore(result: LimuloMatchResult | null, score: LimuloScore): boolean {
  if (result === null) return false;
  return result.scoreA === score.home && result.scoreB === score.away;
}

const scoreOf = (result: LimuloMatchResult | null): LimuloScore | null =>
  result === null || result.scoreA === null || result.scoreB === null
    ? null
    : { home: result.scoreA, away: result.scoreB };

/** Il `resultType` di un referto, o `null` se non c'e' o e' vuoto (un campo vuoto non e' un tipo). */
const resultTypeOf = (result: LimuloMatchResult | null | undefined): string | null =>
  result && typeof result.resultType === 'string' && result.resultType !== '' ? result.resultType : null;

export interface SubmitResultInput {
  /** `POST …/result`. Riceve il punteggio; nient'altro entra nel corpo. */
  submitResult: (score: LimuloScore) => Promise<LimuloCallOutcome<LimuloMatchResultPayload | null>>;
  /** `POST …/result-media`, multipart. Assente = niente prova da allegare. */
  attachEvidence?: (
    clientMutationId: string,
  ) => Promise<LimuloCallOutcome<LimuloMatchResultPayload | null>>;
  /** `GET …/result` — la RILETTURA. Senza questa l'adattatore non puo' fare il suo lavoro. */
  readResult: () => Promise<LimuloCallOutcome<LimuloMatchResultPayload | null>>;
  score: LimuloScore;
  /**
   * L'id che accompagna la prova, uguale a ogni tentativo.
   *
   * ⚠️ **Non e' una garanzia di idempotenza, e non va raccontato come tale.** Il BFF lo
   * inoltra nei metadati dell'asset (`router.js:1952`), ma il provider **non lo persiste sul
   * referto**: `refertoAnnidatoDi` (`incontri.ts`) emette `clientMutationId: ''` di
   * proposito, «non lo persistiamo, e inventarne uno significherebbe far credere al client
   * che la SUA mutazione e' quella registrata». Quindi mandare lo stesso id e' la cosa
   * migliore che il contratto offre, non una prova che il doppio invio venga scartato — ed e'
   * il motivo per cui i tentativi sono POCHI e solo su rifiuti transitori.
   */
  clientMutationId: string;
  /** Il TETTO dei ritentativi (oltre al primo) sul passo 2. Default 2. */
  evidenceMaxRetries?: number;
  /**
   * L'attesa fra un tentativo e il successivo, in millisecondi, iniettabile.
   *
   * Serve a due cose diverse e la seconda vale la prima: in produzione evita che tre
   * tentativi partano nello stesso istante — su un guasto transitorio, tre richieste
   * back-to-back sono tre richieste che falliscono per la stessa ragione, e in piu' pestano
   * un server che sta gia' male. Nel gate viene sostituita con un no-op, cosi' le asserzioni
   * sui ritentativi non pagano un secondo e mezzo di attesa vera.
   */
  sleep?: (ms: number) => Promise<void>;
}

const attesaPredefinita = (ms: number): Promise<void> =>
  new Promise((resolve) => setTimeout(resolve, ms));

/**
 * Il referto e, se c'e', la sua prova — in un tap solo per chi guarda, in due passi veri
 * sulla rete (scelta (b) di Luca, 06/09).
 *
 * Il monolite Laravel postava l'immagine sullo STESSO `POST /result` del punteggio
 * (`services/endpoints/matches.ts:44-51`). Sul BFF sono due rotte: prima il punteggio, poi
 * `result-media`, che registra l'asset presso l'app `media` e lo allega
 * (`router.js:1924-1983`). Se il secondo passo cade, **il referto resta registrato**: dirlo
 * all'utente come un errore generico gli farebbe rifare il primo passo, che e' proprio la
 * cosa che prende il rifiuto muto.
 *
 * ⚠️ Il passo 2 non parte se il passo 1 non ha registrato niente. Il dominio rifiuta le prove
 * senza referto con un nome proprio (`media.senza-risultato`, `http: 409`,
 * `core/risultati.ts:222-223`) — che poi arriva come 424 e non spiega nulla.
 */
export async function submitResultWithEvidence(
  input: SubmitResultInput,
): Promise<LimuloSubmitReport> {
  const { submitResult, attachEvidence, readResult, score, clientMutationId } = input;
  const maxRetries = input.evidenceMaxRetries ?? 2;
  const attendi = input.sleep ?? attesaPredefinita;

  const primo = await submitResult(score);
  const erroreInvio = outcomeError(primo);

  let result: LimuloResultOutcome = 'submitted';
  let resultFailure: LimuloFailure | null = null;
  let resultMatchReason: LimuloMatchReason | null = null;
  let serverScore: LimuloScore | null = null;
  let serverResultType: string | null = null;

  if (erroreInvio !== undefined) {
    resultFailure = limuloFailure(erroreInvio);
    resultMatchReason = matchReasonOf(erroreInvio);
    // LA RILETTURA. Non si giudica dal rifiuto — che non dice niente — ma da cio' che il
    // server HA. Un fallimento della rilettura stessa lascia il dubbio dov'era: e' l'unica
    // risposta onesta, e `refused` e' gia' il nome del dubbio.
    result = 'refused';
    const riletto = await readResult();
    const payload = outcomeData(riletto);
    if (payload !== undefined && payload !== null) {
      const referto = payload.result;
      serverScore = scoreOf(referto);
      serverResultType = resultTypeOf(referto);
      if (sameScore(referto, score)) result = 'already-ours';
      else if (referto !== null) result = 'already-other';
    }
  } else {
    const payload = outcomeData(primo);
    serverScore = payload ? scoreOf(payload.result) : null;
    serverResultType = payload ? resultTypeOf(payload.result) : null;
  }

  // Il referto non risulta registrato ⇒ la prova non ha a cosa attaccarsi.
  const refertoInPiedi = result === 'submitted' || result === 'already-ours';
  if (!attachEvidence) {
    return {
      result,
      evidence: 'skipped',
      resultFailure,
      resultMatchReason,
      evidenceFailure: null,
      serverScore,
      serverResultType,
      evidenceRetries: 0,
    };
  }
  if (!refertoInPiedi) {
    return {
      result,
      evidence: 'skipped',
      resultFailure,
      resultMatchReason,
      evidenceFailure: null,
      serverScore,
      serverResultType,
      evidenceRetries: 0,
    };
  }

  let evidenceFailure: LimuloFailure | null = null;
  let tentativi = 0;
  for (;;) {
    const esito = await attachEvidence(clientMutationId);
    const errore = outcomeError(esito);
    if (errore === undefined) {
      return {
        result,
        evidence: 'attached',
        resultFailure,
        resultMatchReason,
        evidenceFailure: null,
        serverScore,
        serverResultType,
        evidenceRetries: tentativi,
      };
    }
    evidenceFailure = limuloFailure(errore);
    // Si ritenta SOLO cio' che puo' cambiare esito da solo. Un 424, un file troppo grande o
    // di tipo sbagliato darebbero lo stesso rifiuto per sempre: ripeterli fa perdere tempo
    // all'utente e nasconde il messaggio su cui potrebbe agire.
    if (tentativi >= maxRetries || !isRetryableFailure(evidenceFailure)) {
      return {
        result,
        evidence: 'failed',
        resultFailure,
        resultMatchReason,
        evidenceFailure,
        serverScore,
        serverResultType,
        evidenceRetries: tentativi,
      };
    }
    tentativi++;
    // Backoff lineare e corto: 500 ms, poi 1000. Non e' una politica di resilienza, e' il
    // minimo perche' «ritentare» significhi qualcosa di diverso da «rimandare subito».
    await attendi(500 * tentativi);
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// 4. Le altre due azioni: stessa mossa, ma una delle due puo' dire meno
// ─────────────────────────────────────────────────────────────────────────────

/**
 * L'interruttore dell'organizzazione spento (`switch-off`, `E_QLASH_ENDPOINT_DISABLED`, #416) su una
 * SCRITTURA dell'incontro — check-in, «non sono pronto», referto, ritiro.
 *
 * E' un rifiuto che il bordo NOMINA, e lo nomina prima di chiamare il motore: `assertEndpointEnabled`
 * sta in `createRuntimeContext` (`router.js:207`, `policy.js:139` @ `qlash-limulo 7e4d86e`), quindi
 * dietro non e' stato registrato niente. Le frasi del dubbio («il server ha rifiutato senza
 * spiegare», «ricarica la scheda prima di riprovare», «rifiutata senza dirlo») qui direbbero il
 * falso due volte: il server ha spiegato, e riprovare non cambia l'esito (`isRetryableFailure` →
 * `false`).
 *
 * ⚠️ Vale SOLO dove resta il dubbio. Un motivo nominato dal motore (`matchReason`) o un incontro
 * che la rilettura trova deciso restano i fatti piu' forti, e i rami che li dicono vengono prima.
 * Sta qui e non nella sezione 3 perche' serve a tutte e quattro le scritture, referto compreso.
 */
export const spentoSulTenant = (failure: LimuloFailure | null | undefined): boolean =>
  failure?.verdict === 'switch-off';

/**
 * 🔴 `settled-by-forfeit` NON E' «il tuo ritiro e' passato», e il nome lungo serve a
 * impedire esattamente quella lettura.
 *
 * `resultType` dice **che tipo** di referto c'e' — normale, bye, forfeit — e non **di chi**
 * (`incontri.ts:refertoAnnidatoDi`, `resultType: kind`). Se fra il render e la nostra POST
 * fallita si ritira l'AVVERSARIO, il referto e' un forfeit e non e' il nostro: dire «ritiro
 * registrato» a chi ha appena VINTO e' la peggiore delle risposte sbagliate, perche' e'
 * plausibile. E' la stessa deduzione che questo file rifiuta gia' per il check-in
 * (`submitCheckinReconciled`) e che la sezione Limulo di `CLAUDE.md` vieta per iscritto:
 * la parte non e' derivabile lato client, quindi nessuna attribuzione.
 *
 * Il verdetto onesto e' quindi sull'INCONTRO, non sull'attore: «risulta chiuso da un
 * ritiro». Chi legge sa cosa e' successo e non gli viene detto chi e' stato.
 */
export type LimuloForfeitOutcome = 'submitted' | 'settled-by-forfeit' | 'already-other' | 'refused';

export interface LimuloForfeitReport {
  outcome: LimuloForfeitOutcome;
  failure: LimuloFailure | null;
}

/**
 * Il ritiro, con la stessa riconciliazione del referto. Il segno c'e' ed e' riconoscibile —
 * `resultType` esce col `kind` del documento invece di travestirsi da referto normale
 * (`incontri.ts:refertoAnnidatoDi`, «ogni deviazione e' marcata e visibile») — ma dice **che
 * cosa**, non **di chi**: v. la nota su `LimuloForfeitOutcome`. Quindi dopo un rifiuto si
 * rilegge, e si riferisce lo stato dell'INCONTRO senza attribuirlo a nessuno.
 */
export async function submitForfeitReconciled(input: {
  submitForfeit: () => Promise<LimuloCallOutcome<LimuloMatchResultPayload | null>>;
  readMatch: () => Promise<LimuloCallOutcome<LimuloMatch | null>>;
}): Promise<LimuloForfeitReport> {
  const esito = await input.submitForfeit();
  const errore = outcomeError(esito);
  if (errore === undefined) return { outcome: 'submitted', failure: null };

  const failure = limuloFailure(errore);
  const riletto = await input.readMatch();
  const match = outcomeData(riletto);
  if (match === undefined || match === null || match.result === null) {
    return { outcome: 'refused', failure };
  }
  return {
    // ⛔ Nessun ramo «e allora era il mio»: non c'e' nessun dato che lo dica.
    outcome: match.result.resultType === 'forfeit' ? 'settled-by-forfeit' : 'already-other',
    failure,
  };
}

export type LimuloCheckinOutcome = 'submitted' | 'settled' | 'unknown';

export interface LimuloCheckinReport {
  outcome: LimuloCheckinOutcome;
  failure: LimuloFailure | null;
  /**
   * Il motivo NOMINATO del rifiuto (`details.matchReason`, `qlash-limulo#134`), o `null`. Quando
   * c'e' e' la parola del server sulla NOSTRA richiesta, e la schermata la preferisce a cio' che la
   * rilettura lascia intuire; la rilettura si fa lo stesso, e aggiorna lo schermo.
   */
  matchReason: LimuloMatchReason | null;
  /** Chi RISULTA presente dopo la rilettura, per lato. `null` se la rilettura non e' riuscita. */
  presence: { a: boolean; b: boolean } | null;
}

/**
 * Il check-in, riconciliato — **e qui la riconciliazione dice MENO, di proposito**.
 *
 * 🔴 Non possiamo sapere se il check-in registrato e' il NOSTRO. `checkins[].playerId` e'
 * l'id di una PARTE (ADR-0017); `/v1/me` da' un id utente Limulo; i partecipanti non portano
 * `userId`; e `profileId` — l'unica chiave di giunzione immaginabile — il provider lo AZZERA
 * esplicitamente (`giocatoreDi`: `{...partecipanteDa(parte), profileId: ''}`, misurato
 * 2026-09-06). Quindi «risulta presente il Lato A» e' tutto cio' che si puo' dire, e dire
 * «il tuo check-in e' passato» sarebbe una deduzione senza dato sotto.
 *
 * Resta utile: se dopo il rifiuto l'incontro risulta CONCLUSO, il rifiuto ha un nome
 * (`checkin.match-concluso`, `http: 409`, `core/risultati.ts:216-217`) e si puo' dirlo. Fuori
 * da quel caso l'esito e' `unknown` e si mostra la presenza cosi' com'e', senza attribuirla.
 */
export async function submitCheckinReconciled(input: {
  submitCheckin: () => Promise<LimuloCallOutcome<LimuloMatchResultPayload | null>>;
  readMatch: () => Promise<LimuloCallOutcome<LimuloMatch | null>>;
  /** `checkedInSides` di `match.ts`, passata come parametro per non duplicarne la regola. */
  presenceOf: (match: LimuloMatch) => { a: boolean; b: boolean };
}): Promise<LimuloCheckinReport> {
  const esito = await input.submitCheckin();
  const errore = outcomeError(esito);
  if (errore === undefined) {
    return { outcome: 'submitted', failure: null, matchReason: null, presence: null };
  }

  const failure = limuloFailure(errore);
  const matchReason = matchReasonOf(errore);
  const riletto = await input.readMatch();
  const match = outcomeData(riletto);
  if (match === undefined || match === null) {
    return { outcome: 'unknown', failure, matchReason, presence: null };
  }
  const stato = matchStateOf(match);
  return {
    outcome: stato === 'reported' || stato === 'decided' ? 'settled' : 'unknown',
    failure,
    matchReason,
    presence: input.presenceOf(match),
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// 4-bis. «NON SONO PRONTO» — il forfait detto col check-in (qTournaments 1.69.0)
// ─────────────────────────────────────────────────────────────────────────────

/**
 * 🔴 **`declined` NON E' «ho perso»: e' «il server ha accettato la NOSTRA risposta e, rileggendo,
 * l'incontro risulta chiuso».** La prima meta' la da' la POST (e' l'unico fatto su di noi che
 * abbiamo il diritto di conoscere, come per `checkin-done`); la seconda la rilettura.
 *  · `submitted-unconfirmed` — la POST e' passata ma la rilettura non lo mostra (fallita, o il
 *    motore non ha ancora chiuso): si dice che la risposta e' stata accettata, non come e' finita;
 *  · `settled` — la POST e' stata rifiutata e l'incontro risulta concluso;
 *  · `unknown` — rifiutata, e la rilettura non dice di piu'. Se il bordo ha NOMINATO il motivo
 *    (`matchReason`), la schermata dice quello.
 */
export type LimuloNotReadyOutcome = 'declined' | 'submitted-unconfirmed' | 'settled' | 'unknown';

export interface LimuloNotReadyReport {
  outcome: LimuloNotReadyOutcome;
  failure: LimuloFailure | null;
  matchReason: LimuloMatchReason | null;
}

/**
 * «Non sono pronto»: `POST …/checkin` con `{ready: false}` — un forfait IMMEDIATO e DEFINITIVO.
 * Il motore lo prende come risposta finale (@gionko, kb#268: *«una volta che ci e' stato dato il
 * "non sono pronto" vuol dire che e' gia' sicuro che non e' pronto»*): il «sei sicuro?» e' della
 * UI, e sta nel foglio di conferma di `LimuloMatchActions`, non qui.
 *
 * ⚠️ **Si RILEGGE SEMPRE, anche dopo un si'** — a differenza del check-in, che rilegge solo dopo
 * un rifiuto. La ragione e' la conseguenza: un «si'» qui ha chiuso un incontro, e la frase che lo
 * dice deve poggiare su cio' che il server HA adesso, non sull'eco della nostra richiesta.
 */
export async function submitNotReadyReconciled(input: {
  submitNotReady: () => Promise<LimuloCallOutcome<LimuloMatchResultPayload | null>>;
  readMatch: () => Promise<LimuloCallOutcome<LimuloMatch | null>>;
}): Promise<LimuloNotReadyReport> {
  const esito = await input.submitNotReady();
  const errore = outcomeError(esito);
  const riletto = await input.readMatch();
  const letto = outcomeData(riletto) ?? null;
  const stato = letto === null ? null : matchStateOf(letto);
  const concluso = stato === 'reported' || stato === 'decided';

  if (errore === undefined) {
    const chiuso = concluso || letto?.matchCheckIn?.outcome === 'declined';
    return { outcome: chiuso ? 'declined' : 'submitted-unconfirmed', failure: null, matchReason: null };
  }
  return {
    outcome: concluso ? 'settled' : 'unknown',
    failure: limuloFailure(errore),
    matchReason: matchReasonOf(errore),
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// 4-ter. La finestra del check-in dell'incontro: TRE piani, e il primo e' «come oggi»
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Che cosa fa la schermata del check-in dell'incontro.
 *  · `invariato` — `matchCheckIn` assente (il servito di OGGI: il bordo lo scarta fino a
 *    `qlash-limulo#134`), `null` (forma incoerente) o `required: false` (incontro senza finestra,
 *    o un bye). ⇒ **La schermata di prima, riga per riga**: il check-in di sempre, nessun «non sono
 *    pronto», nessun conto alla rovescia. Lo dimostra il banco differenziale della PR.
 *  · `aperto` — richiesto e aperto: il conto alla rovescia su `deadlineAt`, «Sono pronto» e «Non
 *    sono pronto» (col foglio di conferma).
 *  · `chiuso` — richiesto e chiuso: niente bottoni, lo stato in parole secondo `outcome`/`readyAt`.
 *    Porta anche `closedAt`: con `outcome` e `closedAt` entrambi `null` il tempo e' scaduto ma il
 *    torneo lo sta ancora chiudendo (fino a ~60 s, kb#268 08:41:40Z) — non e' «chiuso».
 *  · `pronto` — richiesto e aperto, ma il server dice che il MIO check-in c'e' gia' (`myCheckin ===
 *    true`, `qlash-limulo#134`): niente «Sono pronto» e niente «Non sono pronto» — il secondo
 *    prenderebbe un `409 checkin.already-present` — ma «Sei pronto», e il conto alla rovescia
 *    diventa l'attesa dell'avversario. Con `myCheckin` `false` o `null` resta `aperto`, come prima.
 *
 * ⛔ **Nessun ramo guarda l'orologio.** Una scadenza passata sul telefono NON chiude la finestra:
 * la chiude il server (`open: false`). Fra i due orologi quello che decide e' il suo, e un bottone
 * spento con un secondo d'anticipo e' un incontro perso a tavolino per colpa nostra.
 */
export type LimuloMatchCheckInPlan =
  | { kind: 'invariato' }
  | { kind: 'aperto'; deadlineAt: string }
  | { kind: 'pronto'; deadlineAt: string; readyAt: string | null }
  | {
      kind: 'chiuso';
      outcome: LimuloMatchCheckInOutcome | null;
      readyAt: string | null;
      closedAt: string | null;
    };

export function matchCheckInPlanOf(match: LimuloMatch): LimuloMatchCheckInPlan {
  const ck = match.matchCheckIn ?? null;
  if (ck === null || !ck.required) return { kind: 'invariato' };
  if (ck.open) {
    // `matchCheckInOf` non lascia passare una finestra aperta senza scadenza: questo ramo e' per
    // il tipo, e se mai scattasse la risposta sicura e' la schermata di prima.
    if (ck.deadlineAt === null) return { kind: 'invariato' };
    // Solo il `true` esplicito del server: «non lo sappiamo» (`null`) non spegne un gesto che,
    // mancato, perde l'incontro a tavolino.
    if (match.myCheckin === true) return { kind: 'pronto', deadlineAt: ck.deadlineAt, readyAt: ck.readyAt };
    return { kind: 'aperto', deadlineAt: ck.deadlineAt };
  }
  return { kind: 'chiuso', outcome: ck.outcome, readyAt: ck.readyAt, closedAt: ck.closedAt };
}

// ═════════════════════════════════════════════════════════════════════════════
// BOTTONE SPENTO ≠ NESSUN BOTTONE (reference D2, approvata da Luca I-102)
// ═════════════════════════════════════════════════════════════════════════════

/**
 * Come si PRESENTA un'azione. Tre modi, e non sono tre gradi della stessa cosa.
 *
 * ⭐ **Si SPEGNE un'azione che *esisterebbe* e ora non si puo' fare**: il bottone resta e
 * spiega **perche'** (il check-in gia' registrato, l'attesa della conferma, la disputa, il
 * server che nega). Chi la cerca la trova, col motivo.
 * ⭐ **Si TOGLIE un'azione che in quello stato *non ha senso***: con `resultMode: 'provider'`
 * il referto non e' **bloccato**, **non esiste** — il risultato lo manda il gioco, e non c'e'
 * nessun permesso da spiegare.
 * ⇒ *Confonderle riempie lo schermo di cose grigie che non insegnano niente*, ed e' il
 * difetto che questa funzione esiste per evitare.
 *
 * 📌 **NON contraddice la decisione di Luca del 2026-09-06** («il pannello non si nasconde
 * mai: quando non si puo' premere, lo dice»): la **raffina**. Quella decisione riguarda
 * un'azione **bloccata**, e resta intera — e' il ramo `spento`. D2 aggiunge la distinzione
 * che quella decisione non faceva, fra «bloccata» e «inesistente».
 */
export type LimuloActionPresentation = 'attivo' | 'spento' | 'assente';

/**
 * ⚠️ `provider-result` e' l'UNICO blocco che toglie invece di spegnere, e non per una regola
 * di stile: negli altri casi c'e' un permesso o uno stato da spiegare, qui c'e' un **modo di
 * refertare diverso** — il risultato arriva dal gioco. Un «Invia referto» grigio in
 * `provider` direbbe a un giocatore che gli manca qualcosa, quando non gli manca niente.
 * La frase che serve (`actions.modeProvider`) e' gia' in cima al pannello, e dice l'unica
 * cosa vera: non serve refertare.
 */
const BLOCCHI_CHE_TOLGONO: ReadonlySet<LimuloActionBlock> = new Set(['provider-result']);

export function actionPresentationOf(
  availability: LimuloActionAvailability,
): LimuloActionPresentation {
  if (availability.available) return 'attivo';
  return availability.block !== null && BLOCCHI_CHE_TOLGONO.has(availability.block)
    ? 'assente'
    : 'spento';
}

/**
 * 🔴 **E CI SONO STATI IN CUI IL PANNELLO NON C'E' AFFATTO** — *«turno passato, da comporre,
 * concluso: li' la barra in fondo non c'e' affatto»* (D2).
 *
 * La ragione e' la stessa del caso `assente` applicata a **tutte e tre** le azioni insieme:
 * su un bye non c'e' niente da giocare, su un incontro da comporre manca una delle parti, su
 * uno concluso il referto c'e' gia' e il primo vince. In nessuno dei tre c'e' **un'azione
 * bloccata da spiegare**: c'e' un incontro che non chiede niente a nessuno. Tre righe grigie
 * col loro motivo sarebbero tre modi diversi di dire «non c'e' niente da fare», scritti dove
 * l'utente cerca qualcosa da fare.
 *
 * ⚠️ **`disputed` NON e' in questa lista**, ed e' la distinzione che costa: li' il referto e'
 * spento (col suo motivo) ma **check-in e forfeit restano concessi dal server** — misurato su
 * una disputa vera il 2026-09-15. Togliere il pannello toglierebbe due porte aperte.
 * ⚠️ E nemmeno `proposed`: li' il pannello ospita la conferma, che e' il momento piu'
 * importante dell'intera schermata.
 */
export function matchActionsPanelVisible(match: LimuloMatch): boolean {
  const state = matchStateOf(match);
  return state !== 'bye' && state !== 'incomplete' && state !== 'reported' && state !== 'decided';
}

/**
 * I tre verdetti di un punteggio davanti al tetto che il torneo dichiara.
 *
 * ⚠️ **`unknown` non e' un riempitivo**, e' la risposta piu' frequente: `maxScore` e' `null`
 * ogni volta che il torneo non dichiara un tetto **e** ogni volta che il torneo non e' ancora
 * arrivato. Un `boolean` schiaccerebbe «non lo so» su «va bene», ed e' la stessa regola di
 * `eligibility.ts` e di `emptyMatchesReasonOf`: *«non lo so» e' un verdetto, e va detto.*
 */
export type LimuloScoreCapVerdict = 'within' | 'over' | 'unknown';

/**
 * Un punteggio sta dentro il tetto dichiarato dal torneo?
 *
 * 🔴 **IMPIANTO, NON COMPORTAMENTO.** Questa funzione esiste e **nessuna schermata la chiama
 * ancora**, di proposito: *cosa* fare di un punteggio oltre il tetto — rifiutare l'invio,
 * oppure avvisare e mandare lo stesso — **e' una domanda di prodotto e non di disegno**
 * (tma-orc, 2026-09-20 23:0xZ: decide cosa succede a chi ha vinto 12-3 in un torneo che
 * dichiara 10), ed e' davanti a Luca. ⛔ Chi la collega a un bottone senza quella risposta sta
 * prendendo la decisione al posto suo.
 *
 * ⚠️ **Il tetto vale su OGNI lato, non sulla somma**: e' un tetto di punteggio, non di
 * partita. `risultati.js:247` lo applica cosi' in scrittura.
 * ⚠️ **Il confronto e' `>`, non `>=`**: un punteggio *pari* al tetto e' dentro. Un tetto e' il
 * massimo ammesso, non il primo valore vietato.
 * ⛔ E **non si controlla che siano interi non negativi**: il dominio ammette di proposito
 * punteggi negativi e frazionari (ADR-0063/A5), e una guardia in piu' qui rifiuterebbe
 * punteggi che il server accetta.
 */
export function scoreOverCapOf(input: {
  home: number;
  away: number;
  /** `null`/`undefined` = nessun tetto dichiarato, **o** torneo non arrivato. Non si deduce. */
  maxScore?: number | null;
}): LimuloScoreCapVerdict {
  const tetto = input.maxScore;
  if (typeof tetto !== 'number' || !Number.isFinite(tetto)) return 'unknown';
  // ⚠️ Un punteggio non finito (campo vuoto, `NaN` da un `Number('')`) non e' «dentro»: il
  // modulo lo rifiuta gia' per conto suo, e rispondere `within` qui direbbe che un dato che
  // non c'e' rispetta il tetto.
  if (!Number.isFinite(input.home) || !Number.isFinite(input.away)) return 'unknown';
  return input.home > tetto || input.away > tetto ? 'over' : 'within';
}
